import React, { useState, useEffect } from 'react';
import { supabase } from '../../utils/supabaseClient';
import { useAuth } from '../../contexts/AuthContext';
import '../../styles/Transactions.css';
import { FaArrowLeft, FaRupeeSign, FaMoneyBillWave, FaSearch, FaEdit, FaReceipt, FaCheckCircle, FaTimesCircle, FaColumns, FaLayerGroup } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import EditTransactionModal from './EditTransactionModal';
import DateRangePicker from '../common/DateRangePicker';

const Transactions = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [transactions, setTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchColumn, setSearchColumn] = useState('all');
  const [showColumnDropdown, setShowColumnDropdown] = useState(false);
  const [paymentMethodFilter, setPaymentMethodFilter] = useState('all');
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [toast, setToast] = useState(null); // { msg, type }
  const [activeFilter, setActiveFilter] = useState('active');
  const [showActiveDropdown, setShowActiveDropdown] = useState(false);
  const [sortField, setSortField] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');
  const [groupBy, setGroupBy] = useState('none');
  const [showGroupDropdown, setShowGroupDropdown] = useState(false);
  const [collapsedGroups, setCollapsedGroups] = useState({});
  // Default to today's date
  const todayStr = new Date().toISOString().split('T')[0];
  const [customDateFrom, setCustomDateFrom] = useState(todayStr);
  const [customDateTo, setCustomDateTo] = useState(todayStr);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  useEffect(() => {
    fetchTransactions();
  }, []);

  useEffect(() => {
    filterTransactions();
  }, [transactions, searchTerm, searchColumn, paymentMethodFilter, activeFilter, customDateFrom, customDateTo]);

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showActiveDropdown && !e.target.closest('.active-filter-container')) {
        setShowActiveDropdown(false);
      }
      if (showColumnDropdown && !e.target.closest('.txn-column-dropdown')) {
        setShowColumnDropdown(false);
      }
      if (showGroupDropdown && !e.target.closest('.group-by-container')) {
        setShowGroupDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showActiveDropdown, showColumnDropdown, showGroupDropdown]);

  const GROUP_OPTIONS = [
    { value: 'none', label: 'No Grouping', icon: 'fa-list' },
    { value: 'student_rno', label: 'Roll No', icon: 'fa-id-badge' },
    { value: 'student_class', label: 'Class', icon: 'fa-chalkboard' },
    { value: 'payment_method', label: 'Method', icon: 'fa-money-bill-wave' },
  ];

  // Reset collapsed groups when groupBy changes
  useEffect(() => {
    setCollapsedGroups({});
  }, [groupBy]);

  const toggleGroup = (key) => {
    setCollapsedGroups(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // Build grouped data
  const buildGroups = (rows) => {
    if (groupBy === 'none') return null;
    const groups = {};
    rows.forEach(row => {
      let key;
      if (groupBy === 'student_rno') {
        key = `${row.student_rno} — ${row.student_name}`;
      } else if (groupBy === 'student_class') {
        key = row.student_class || 'N/A';
      } else if (groupBy === 'payment_method') {
        key = row.payment_method ? row.payment_method.charAt(0).toUpperCase() + row.payment_method.slice(1).toLowerCase() : 'N/A';
      } else {
        key = String(row[groupBy] ?? 'N/A');
      }
      if (!groups[key]) groups[key] = [];
      groups[key].push(row);
    });
    // Sort group keys
    const sorted = Object.entries(groups).sort(([a], [b]) => a.localeCompare(b, undefined, { numeric: true }));
    return sorted;
  };

  const getGroupSubtotals = (rows) => {
    const totalPaid = rows.reduce((s, r) => s + (parseFloat(r.amount_paid) || 0), 0);
    const totalDue = rows.reduce((s, r) => s + (parseFloat(r.total_amount) || 0), 0);
    return { count: rows.length, totalPaid, totalDue };
  };

  const SEARCH_COLUMNS = [
    { value: 'all', label: 'All Columns' },
    { value: 'student_name', label: 'Student Name' },
    { value: 'student_rno', label: 'Roll No' },
    { value: 'student_class', label: 'Class' },
    { value: 'amount_paid', label: 'Amount Paid' },
    { value: 'payment_method', label: 'Payment Method' },
    { value: 'created_by', label: 'Created By' },
    { value: 'id', label: 'Transaction ID' },
  ];

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      
      // Fetch transactions with student details
      const { data: transactionsData, error: transError } = await supabase
        .from('payment_transactions')
        .select('*')
        .order('created_at', { ascending: false });

      if (transError) throw transError;

      // Fetch student names and active status
      const { data: studentsData, error: studentError } = await supabase
        .from('students')
        .select('Rno, Name, Class, Active');

      if (studentError) throw studentError;

      // Merge data
      const mergedData = transactionsData.map(transaction => {
        const student = studentsData.find(s => s.Rno === transaction.student_rno);
        return {
          ...transaction,
          student_name: student?.Name || 'Unknown',
          student_class: student?.Class || 'N/A',
          student_active: student?.Active !== false
        };
      });

      setTransactions(mergedData);
    } catch (error) {
      console.error('Error fetching transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterTransactions = () => {
    let filtered = [...transactions];

    // Active filter
    if (activeFilter === 'active') {
      filtered = filtered.filter(t => t.student_active !== false);
    } else if (activeFilter === 'inactive') {
      filtered = filtered.filter(t => t.student_active === false);
    }

    // Search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(transaction => {
        if (searchColumn === 'all') {
          return (
            transaction.student_name?.toLowerCase().includes(searchLower) ||
            transaction.student_rno?.toString().includes(searchTerm) ||
            transaction.student_class?.toLowerCase().includes(searchLower) ||
            transaction.amount_paid?.toString().includes(searchTerm) ||
            transaction.payment_method?.toLowerCase().includes(searchLower) ||
            transaction.created_by?.toLowerCase().includes(searchLower) ||
            transaction.id?.toLowerCase().includes(searchLower)
          );
        }
        const val = transaction[searchColumn];
        return val != null && String(val).toLowerCase().includes(searchLower);
      });
    }

    // Date filter
    if (customDateFrom && customDateTo) {
      filtered = filtered.filter(transaction => {
        const transDate = new Date(transaction.created_at);
        const from = new Date(customDateFrom);
        from.setHours(0, 0, 0, 0);
        const to = new Date(customDateTo);
        to.setHours(23, 59, 59, 999);
        return transDate >= from && transDate <= to;
      });
    }

    // Payment method filter
    if (paymentMethodFilter !== 'all') {
      filtered = filtered.filter(transaction => 
        transaction.payment_method?.toLowerCase() === paymentMethodFilter.toLowerCase()
      );
    }

    setFilteredTransactions(filtered);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedTransactions = [...filteredTransactions].sort((a, b) => {
    let valA = a[sortField];
    let valB = b[sortField];
    if (valA == null) return sortDirection === 'asc' ? -1 : 1;
    if (valB == null) return sortDirection === 'asc' ? 1 : -1;
    if (sortField === 'created_at') {
      return sortDirection === 'asc'
        ? new Date(valA) - new Date(valB)
        : new Date(valB) - new Date(valA);
    }
    if (typeof valA === 'string') {
      const cmp = valA.localeCompare(valB, undefined, { sensitivity: 'base' });
      return sortDirection === 'asc' ? cmp : -cmp;
    }
    if (typeof valA === 'number') {
      return sortDirection === 'asc' ? valA - valB : valB - valA;
    }
    return 0;
  });

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const getTotalCollection = () => {
    return filteredTransactions.reduce((sum, trans) => sum + (parseFloat(trans.amount_paid) || 0), 0);
  };

  const renderRow = (transaction) => (
    <tr
      key={transaction.id}
      onDoubleClick={() => navigate(`/academic/transaction/${transaction.id}`)}
      style={{ cursor: 'pointer' }}
      title="Double-click to view receipt"
    >
      <td className="actions-cell" onDoubleClick={e => e.stopPropagation()}>
        <div className="row-actions">
          <button
            className="row-action-btn row-action-view"
            title="View receipt"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/academic/transaction/${transaction.id}`);
            }}
          >
            <FaReceipt />
          </button>
          <button
            className="row-action-btn row-action-edit"
            title="Edit transaction"
            onClick={(e) => {
              e.stopPropagation();
              setEditingTransaction(transaction);
            }}
          >
            <FaEdit />
          </button>
        </div>
      </td>
      <td className="date-cell">{formatDate(transaction.created_at)}</td>
      <td className="student-name">{transaction.student_name}</td>
      <td>{transaction.student_rno}</td>
      <td><span className="class-badge">{transaction.student_class}</span></td>
      <td className={transaction.fee_dues < 0 ? 'negative-amount' : transaction.fee_dues > 0 ? 'positive-amount' : ''}>
        {transaction.fee_dues < 0 ? '-' : ''}₹{Math.abs(transaction.fee_dues || 0)}
      </td>
      <td>₹{transaction.current_month_fee || 0}</td>
      <td>₹{transaction.transport_fee || 0}</td>
      <td>₹{transaction.examination_fee || 0}</td>
      <td className="total-due">₹{transaction.total_amount || 0}</td>
      <td className="amount-paid">₹{transaction.amount_paid || 0}</td>
      <td className={transaction.discount && transaction.discount !== 0 ? 'negative-amount' : 'zero-amount'}>
        {transaction.discount && transaction.discount !== 0 ? '−' : ''}₹{Math.abs(transaction.discount || 0)}
      </td>
      <td className="other-fees-cell">
        {transaction.other && transaction.other.length > 0 ? (
          <div className="other-fee-pills">
            {transaction.other.map((item, i) => (
              <span key={i} className="other-fee-pill">{item.label}: ₹{Math.abs(item.value)}</span>
            ))}
          </div>
        ) : '—'}
      </td>
      <td className={transaction.new_dues < 0 ? 'negative-amount' : transaction.new_dues > 0 ? 'positive-amount' : 'zero-amount'}>
        {transaction.new_dues < 0 && '−'}
        {transaction.new_dues > 0 && '+'}
        ₹{Math.abs(transaction.new_dues || 0)}
      </td>
      <td>
        <span className={`method-badge ${transaction.payment_method?.toLowerCase()}`}>
          {transaction.payment_method || 'N/A'}
        </span>
      </td>
      <td className="created-by">{transaction.created_by || 'N/A'}</td>
    </tr>
  );

  if (loading) {
    return (
      <div className="transactions-container">
        <div className="transactions-header">
          <h1><FaRupeeSign /> Payment Transactions</h1>
        </div>
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading transactions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="transactions-container">
      {/* Header */}
      <div className="transactions-header">
        <div className="header-left">
          <button className="back-btn" onClick={() => navigate('/academic/fees')}>
            <FaArrowLeft /> Back to Fees
          </button>
          <h1><FaRupeeSign /> Payment Transactions</h1>
        </div>
        {user?.role === 'admin' && (
          <div className="header-stats">
            <div className="stat-item">
              <span className="stat-label">Total Transactions:</span>
              <span className="stat-value">{filteredTransactions.length}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Total Collection:</span>
              <span className="stat-value total-amount">₹{Math.round(getTotalCollection())}</span>
            </div>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="transactions-filters">
        <div className="filter-row">
          <div className="filter-group">
            <FaSearch className="filter-icon" />
            <input
              type="text"
              className="search-input"
              placeholder={`Search ${searchColumn === 'all' ? 'all columns' : SEARCH_COLUMNS.find(c => c.value === searchColumn)?.label}...`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="txn-column-dropdown" style={{ position: 'relative' }}>
            <button
              className={`txn-col-select-btn ${searchColumn !== 'all' ? 'filtered' : ''}`}
              onClick={() => setShowColumnDropdown(!showColumnDropdown)}
            >
              <FaColumns />
              <span>{SEARCH_COLUMNS.find(c => c.value === searchColumn)?.label || 'All Columns'}</span>
              <i className={`fas fa-chevron-${showColumnDropdown ? 'up' : 'down'}`}></i>
            </button>
            {showColumnDropdown && (
              <div className="txn-col-dropdown-menu">
                {SEARCH_COLUMNS.map(opt => (
                  <div
                    key={opt.value}
                    className={`txn-col-dropdown-item ${searchColumn === opt.value ? 'selected' : ''}`}
                    onClick={() => { setSearchColumn(opt.value); setShowColumnDropdown(false); }}
                  >
                    {opt.label}
                  </div>
                ))}
              </div>
            )}
          </div>

          <DateRangePicker
            from={customDateFrom}
            to={customDateTo}
            onChange={(fromVal, toVal) => {
              setCustomDateFrom(fromVal);
              setCustomDateTo(toVal);
            }}
          />

          <div className="filter-group">
            <FaMoneyBillWave className="filter-icon" />
            <select
              className="transactions-filter-select"
              value={paymentMethodFilter}
              onChange={(e) => setPaymentMethodFilter(e.target.value)}
            >
              <option value="all">All Methods</option>
              <option value="cash">Cash</option>
              <option value="online">Online</option>
            </select>
          </div>

          {/* Group By */}
          <div className="group-by-container" style={{ position: 'relative' }}>
            <button
              className={`group-by-btn ${groupBy !== 'none' ? 'active' : ''}`}
              onClick={() => setShowGroupDropdown(!showGroupDropdown)}
            >
              <FaLayerGroup />
              <span>{GROUP_OPTIONS.find(o => o.value === groupBy)?.label || 'Group By'}</span>
              <i className={`fas fa-chevron-${showGroupDropdown ? 'up' : 'down'}`}></i>
            </button>
            {showGroupDropdown && (
              <div className="group-by-dropdown">
                {GROUP_OPTIONS.map(opt => (
                  <div
                    key={opt.value}
                    className={`group-by-option ${groupBy === opt.value ? 'selected' : ''}`}
                    onClick={() => { setGroupBy(opt.value); setShowGroupDropdown(false); }}
                  >
                    <i className={`fas ${opt.icon}`}></i>
                    <span>{opt.label}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Active/Inactive Filter */}
          <div className="active-filter-container" style={{ position: 'relative' }}>
            <button
              className={`active-filter-btn ${activeFilter !== 'active' ? 'filtered' : ''}`}
              onClick={() => setShowActiveDropdown(!showActiveDropdown)}
            >
              <i className={`fas ${activeFilter === 'active' ? 'fa-toggle-on' : activeFilter === 'inactive' ? 'fa-toggle-off' : 'fa-users'}`}></i>
              <span>{activeFilter === 'active' ? 'Active' : activeFilter === 'inactive' ? 'Inactive' : 'All'}</span>
              <i className={`fas fa-chevron-${showActiveDropdown ? 'up' : 'down'}`}></i>
            </button>
            {showActiveDropdown && (
              <div className="active-filter-dropdown">
                {[
                  { value: 'active', label: 'Active', icon: 'fa-toggle-on' },
                  { value: 'inactive', label: 'Inactive', icon: 'fa-toggle-off' },
                  { value: 'all', label: 'All', icon: 'fa-users' },
                ].map(opt => (
                  <div
                    key={opt.value}
                    className={`active-filter-option ${activeFilter === opt.value ? 'selected' : ''}`}
                    onClick={() => { setActiveFilter(opt.value); setShowActiveDropdown(false); }}
                  >
                    <i className={`fas ${opt.icon}`}></i>
                    <span>{opt.label}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Transactions Table */}
      <div className="transactions-table-container">
        {filteredTransactions.length === 0 ? (
          <div className="no-data">
            <p>No transactions found</p>
          </div>
        ) : (
          <table className="transactions-table">
            <thead>
              <tr>
                <th className="actions-col">Actions</th>
                <th className="sortable-th" onClick={() => handleSort('created_at')}>Date & Time {sortField === 'created_at' && <i className={`fas fa-sort-${sortDirection === 'asc' ? 'up' : 'down'}`}></i>}</th>
                <th className="sortable-th" onClick={() => handleSort('student_name')}>Student {sortField === 'student_name' && <i className={`fas fa-sort-${sortDirection === 'asc' ? 'up' : 'down'}`}></i>}</th>
                <th className="sortable-th" onClick={() => handleSort('student_rno')}>Roll No {sortField === 'student_rno' && <i className={`fas fa-sort-${sortDirection === 'asc' ? 'up' : 'down'}`}></i>}</th>
                <th className="sortable-th" onClick={() => handleSort('student_class')}>Class {sortField === 'student_class' && <i className={`fas fa-sort-${sortDirection === 'asc' ? 'up' : 'down'}`}></i>}</th>
                <th className="sortable-th" onClick={() => handleSort('fee_dues')}>Previous Dues {sortField === 'fee_dues' && <i className={`fas fa-sort-${sortDirection === 'asc' ? 'up' : 'down'}`}></i>}</th>
                <th className="sortable-th" onClick={() => handleSort('current_month_fee')}>Monthly Fee {sortField === 'current_month_fee' && <i className={`fas fa-sort-${sortDirection === 'asc' ? 'up' : 'down'}`}></i>}</th>
                <th className="sortable-th" onClick={() => handleSort('transport_fee')}>Transport {sortField === 'transport_fee' && <i className={`fas fa-sort-${sortDirection === 'asc' ? 'up' : 'down'}`}></i>}</th>
                <th className="sortable-th" onClick={() => handleSort('examination_fee')}>Exam {sortField === 'examination_fee' && <i className={`fas fa-sort-${sortDirection === 'asc' ? 'up' : 'down'}`}></i>}</th>
                <th className="sortable-th" onClick={() => handleSort('total_amount')}>Total Due {sortField === 'total_amount' && <i className={`fas fa-sort-${sortDirection === 'asc' ? 'up' : 'down'}`}></i>}</th>
                <th className="sortable-th" onClick={() => handleSort('amount_paid')}>Amount Paid {sortField === 'amount_paid' && <i className={`fas fa-sort-${sortDirection === 'asc' ? 'up' : 'down'}`}></i>}</th>
                <th className="sortable-th" onClick={() => handleSort('discount')}>Discount {sortField === 'discount' && <i className={`fas fa-sort-${sortDirection === 'asc' ? 'up' : 'down'}`}></i>}</th>
                <th>Other</th>
                <th className="sortable-th" onClick={() => handleSort('new_dues')}>New Dues {sortField === 'new_dues' && <i className={`fas fa-sort-${sortDirection === 'asc' ? 'up' : 'down'}`}></i>}</th>
                <th className="sortable-th" onClick={() => handleSort('payment_method')}>Method {sortField === 'payment_method' && <i className={`fas fa-sort-${sortDirection === 'asc' ? 'up' : 'down'}`}></i>}</th>
                <th className="sortable-th" onClick={() => handleSort('created_by')}>Created By {sortField === 'created_by' && <i className={`fas fa-sort-${sortDirection === 'asc' ? 'up' : 'down'}`}></i>}</th>
              </tr>
            </thead>
            <tbody>
              {groupBy === 'none' ? (
                sortedTransactions.map((transaction) => renderRow(transaction))
              ) : (
                buildGroups(sortedTransactions)?.map(([groupKey, rows]) => {
                  const sub = getGroupSubtotals(rows);
                  const isCollapsed = collapsedGroups[groupKey];
                  return (
                    <React.Fragment key={groupKey}>
                      <tr className="group-header-row" onClick={() => toggleGroup(groupKey)}>
                        <td colSpan={16}>
                          <div className="group-header-content">
                            <i className={`fas fa-chevron-${isCollapsed ? 'right' : 'down'} group-chevron`}></i>
                            <span className="group-header-label">{groupKey}</span>
                            <span className="group-header-stats">
                              <span className="group-stat">{sub.count} txn{sub.count !== 1 ? 's' : ''}</span>
                              <span className="group-stat group-stat-due">Due: ₹{Math.round(sub.totalDue)}</span>
                              <span className="group-stat group-stat-paid">Paid: ₹{Math.round(sub.totalPaid)}</span>
                            </span>
                          </div>
                        </td>
                      </tr>
                      {!isCollapsed && rows.map((transaction) => renderRow(transaction))}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Edit Transaction Modal */}
      {editingTransaction && (
        <EditTransactionModal
          transaction={editingTransaction}
          onClose={() => setEditingTransaction(null)}
          onSaved={fetchTransactions}
          onNotify={showToast}
        />
      )}

      {/* Toast */}
      {toast && (
        <div className={`txn-toast txn-toast-${toast.type}`}>
          {toast.type === 'success' && <FaCheckCircle />}
          {toast.type === 'error' && <FaTimesCircle />}
          <span>{toast.msg}</span>
        </div>
      )}
    </div>
  );
};

export default Transactions;
